import React, { useState } from 'react';
import { Alert, Button, Card, Col, Descriptions, Row, Space, Tag, Typography } from 'antd';
import * as Sentry from '@sentry/react';

type DiagnosticState = 'idle' | 'sending' | 'success' | 'warning' | 'error';

type DiagnosticResult = {
    state: DiagnosticState;
    message: string;
    eventId?: string;
};

type DiagnosticKind = 'message' | 'error';

const FLUSH_TIMEOUT_MS = 5000;

const initialResult: DiagnosticResult = {
    state: 'idle',
    message: 'Aún no se ha enviado ningún evento de diagnóstico.',
};

function SentryDiagnostics(){
    const [result, setResult] = useState<DiagnosticResult>(initialResult);
    const [activeDiagnostic, setActiveDiagnostic] = useState<DiagnosticKind | null>(null);
    const sentryInitialized = Sentry.isInitialized();
    const sentryClient = Sentry.getClient();
    const sentryEnabled = Boolean(
        sentryClient
        && sentryClient.getOptions().enabled !== false
        && sentryClient.getDsn()
    );
    const environment = process.env.REACT_APP_ENV || 'no definido';
    const isSending = activeDiagnostic !== null;

    const runDiagnostic = async (kind: DiagnosticKind) => {
        const activeClient = Sentry.getClient();
        const isEnabled = Boolean(
            activeClient
            && activeClient.getOptions().enabled !== false
            && activeClient.getDsn()
        );

        if(!isEnabled){
            setResult({
                state: 'error',
                message: 'Sentry no está habilitado. Revisa REACT_APP_ENV y REACT_APP_SENTRY_URL en este despliegue.',
            });
            return;
        }

        setResult({
            state: 'sending',
            message: 'Enviando el evento y esperando a que termine la cola de Sentry...',
        });
        setActiveDiagnostic(kind);

        try{
            const captureContext = {
                tags: {
                    app: 'ui',
                    diagnostic: 'sentry-connectivity',
                    app_environment: environment,
                },
                extra: {
                    initiatedAt: new Date().toISOString(),
                    route: window.location.pathname,
                },
            };

            let eventId: string;

            if(kind === 'message'){
                eventId = Sentry.captureMessage(
                    'Prueba manual de conexión con Sentry (ui)',
                    { ...captureContext, level: 'info' }
                );
            }else{
                try{
                    throw new Error('Error controlado para validar la conexión con Sentry (ui)');
                }catch(error){
                    eventId = Sentry.captureException(error, captureContext);
                }
            }

            const queueFlushed = await Sentry.flush(FLUSH_TIMEOUT_MS);

            setResult({
                state: queueFlushed ? 'success' : 'warning',
                message: queueFlushed
                    ? 'El SDK procesó el evento y vació su cola. Confirma su recepción en Sentry con el event ID.'
                    : 'El SDK generó el evento, pero no pudo vaciar su cola dentro de 5 segundos.',
                eventId,
            });
        }catch(error){
            setResult({
                state: 'error',
                message: error instanceof Error
                    ? `No se pudo completar la prueba: ${error.message}`
                    : 'No se pudo completar la prueba por un error desconocido.',
            });
        }finally{
            setActiveDiagnostic(null);
        }
    };

    const resultType = result.state === 'success'
        ? 'success'
        : result.state === 'error'
            ? 'error'
            : result.state === 'warning'
                ? 'warning'
                : 'info';

    return (
        <Row>
            <Col xs={24} md={{span: 18, offset: 3}} lg={{span: 14, offset: 5}}>
                <Card title="Diagnóstico de Sentry">
                    <Typography.Paragraph>
                        Esta página envía eventos deliberados y etiquetados al proyecto configurado para este frontend.
                        El error de prueba se captura de forma controlada y no interrumpe la aplicación.
                    </Typography.Paragraph>

                    <Descriptions bordered column={1} size="small" className="mb-4">
                        <Descriptions.Item label="Frontend">ui</Descriptions.Item>
                        <Descriptions.Item label="Entorno">{environment}</Descriptions.Item>
                        <Descriptions.Item label="SDK inicializado">
                            <Tag color={sentryInitialized ? 'green' : 'red'}>
                                {sentryInitialized ? 'Sí' : 'No'}
                            </Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Transporte habilitado">
                            <Tag color={sentryEnabled ? 'green' : 'red'}>
                                {sentryEnabled ? 'Sí' : 'No'}
                            </Tag>
                        </Descriptions.Item>
                    </Descriptions>

                    {!sentryEnabled && (
                        <Alert
                            className="mb-4"
                            type="warning"
                            showIcon
                            message="Sentry no está disponible en este despliegue"
                            description="En desarrollo local se deshabilita cuando REACT_APP_ENV es dev. En otro entorno, revisa que REACT_APP_SENTRY_URL esté configurada al compilar."
                        />
                    )}

                    <Space wrap className="mb-4">
                        <Button
                            type="primary"
                            disabled={!sentryEnabled || isSending}
                            loading={activeDiagnostic === 'message'}
                            onClick={() => runDiagnostic('message')}
                        >
                            Capturar mensaje
                        </Button>
                        <Button
                            danger
                            disabled={!sentryEnabled || isSending}
                            loading={activeDiagnostic === 'error'}
                            onClick={() => runDiagnostic('error')}
                        >
                            Capturar error controlado
                        </Button>
                    </Space>

                    <Alert
                        type={resultType}
                        showIcon
                        message="Resultado de la última prueba"
                        description={(
                            <Space direction="vertical" size={0}>
                                <span>{result.message}</span>
                                {result.eventId && (
                                    <Typography.Text code copyable data-testid="sentry-event-id">
                                        {result.eventId}
                                    </Typography.Text>
                                )}
                            </Space>
                        )}
                    />
                </Card>
            </Col>
        </Row>
    );
}

export default SentryDiagnostics;
